import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./styles.css";

const Header = ({ isAuthenticated, setIsAuthenticated }) => {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [userName, setUserName] = useState("");
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to load user data from localStorage
  const loadUserData = () => {
    const user = localStorage.getItem("userDetails");
    const profile = localStorage.getItem("userProfile");

    if (user || profile) {
      try {
        // Try to get name from profile first, then fall back to userDetails
        let name = "";

        if (profile) {
          const profileData = JSON.parse(profile);
          name = profileData.name || "";
        }

        if (!name && user) {
          const userData = JSON.parse(user);
          name = userData.name || userData.email;
        }

        if (name) {
          setIsLoggedIn(true);
          setUserName(name);
        } else {
          setIsLoggedIn(false);
          setUserName("");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setIsLoggedIn(false);
        setUserName("");
      }
    } else {
      setIsLoggedIn(false);
      setUserName("");
    }
  };

  // Update login state when isAuthenticated prop changes or location changes
  useEffect(() => {
    loadUserData();
  }, [isAuthenticated, location.pathname]);

  // Listen for storage changes (when profile is updated in another component)
  useEffect(() => {
    const handleStorageChange = () => {
      loadUserData();
    };

    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom event (for same-page updates)
    window.addEventListener("userProfileUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userProfileUpdated", handleStorageChange);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileDropdown]);

  const handleLogout = () => {
    localStorage.removeItem("userDetails");
    localStorage.removeItem("userProfile");
    setIsLoggedIn(false);
    setUserName("");
    setShowProfileDropdown(false);

    if (setIsAuthenticated) {
      setIsAuthenticated(false);
    }

    setShowPopup(true);
    setShowMobileSidebar(false);

    setTimeout(() => {
      setShowPopup(false);
      navigate("/");
    }, 1500);
  };

  const toggleMobileSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar);
  };

  const closeMobileSidebar = () => {
    setShowMobileSidebar(false);
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const handleDropdownItemClick = () => {
    setShowProfileDropdown(false);
  };

  const getNavigationLinks = () => {
    const publicLinks = [
      { label: "Home", path: "/" },
      { label: "About", path: "/about" },
      { label: "Contact", path: "/contact" },
    ];

    if (isLoggedIn) {
      return [
        ...publicLinks,
        { label: "AI Search", path: "/ai-search" },
        { label: "Visual Search AI", path: "/visual-search", badge: "NEW" },
        {
          label: "🧠 Deep Think Advisor",
          path: "/deep-think-advisor",
          badge: "NEW",
        },
        { label: "Compare", path: "/compare" },
      ];
    }

    return publicLinks;
  };

  // Get first letter of user name for avatar
  const getAvatarLetter = () => {
    return userName.charAt(0).toUpperCase() || "U";
  };

  return (
    <>
      {/* Logout Success Popup */}
      {showPopup && (
        <div className="logout-popup">
          <div className="logout-popup-content">
            <i className="fas fa-check-circle"></i>
            Logged out successfully!
          </div>
        </div>
      )}

      <header className="header">
        <div className="header-container">
          {/* Logo */}
          <Link to="/" className="logo" onClick={closeMobileSidebar}>
            <i className="fas fa-cube"></i>
            <span>AR Bazaar</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {getNavigationLinks().map((link, index) => (
              <Link key={index} to={link.path} className="nav-link">
                {link.label}
                {link.badge && (
                  <span className="gemini3-badge-nav">{link.badge}</span>
                )}
              </Link>
            ))}
          </nav>

          {/* Auth Section */}
          <div className="auth-section">
            {isLoggedIn ? (
              <div className="profile-dropdown-wrapper" ref={dropdownRef}>
                <button
                  className="user-profile-button"
                  onClick={toggleProfileDropdown}
                  aria-label="User profile menu"
                >
                  <div className="user-avatar-circle">{getAvatarLetter()}</div>
                  <span className="user-display-name">{userName}</span>
                </button>

                {showProfileDropdown && (
                  <div className="profile-dropdown-menu">
                    <div className="dropdown-user-header">
                      <div className="dropdown-avatar-large">
                        {getAvatarLetter()}
                      </div>
                      <div className="dropdown-user-details">
                        <strong>{userName}</strong>
                        <span className="view-profile-text">
                          Manage your account
                        </span>
                      </div>
                    </div>

                    <div className="dropdown-divider"></div>

                    <Link
                      to="/profile"
                      className="dropdown-menu-item"
                      onClick={handleDropdownItemClick}
                    >
                      <span className="dropdown-item-icon">👤</span>
                      <span>My Profile</span>
                    </Link>

                    <Link
                      to="/order-history"
                      className="dropdown-menu-item"
                      onClick={handleDropdownItemClick}
                    >
                      <span className="dropdown-item-icon">📦</span>
                      <span>Order History</span>
                    </Link>

                    <Link
                      to="/wishlist"
                      className="dropdown-menu-item"
                      onClick={handleDropdownItemClick}
                    >
                      <span className="dropdown-item-icon">❤️</span>
                      <span>Wishlist</span>
                    </Link>

                    <div className="dropdown-divider"></div>

                    <button
                      className="dropdown-menu-item logout-menu-item"
                      onClick={handleLogout}
                    >
                      <span className="dropdown-item-icon">🚪</span>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <Link to="/sign-in" className="auth-btn sign-in-btn">
                  <i className="fas fa-sign-in-alt"></i>
                  Login
                </Link>
                <Link to="/sign-up" className="auth-btn sign-up-btn">
                  <i className="fas fa-user-plus"></i>
                  Sign Up
                </Link>
              </div>
            )}

            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileSidebar}
              aria-label="Toggle mobile menu"
            >
              <i className="fas fa-bars"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <div className={`mobile-sidebar ${showMobileSidebar ? "active" : ""}`}>
        <div className="mobile-sidebar-header">
          {isLoggedIn ? (
            <div className="mobile-user-info">
              <i className="fas fa-user-circle"></i>
              {userName}
            </div>
          ) : (
            <div className="mobile-logo">
              <i className="fas fa-cube"></i>
              <span>AR Bazaar</span>
            </div>
          )}

          {/* Close Button */}
          <button
            className="mobile-sidebar-close-btn"
            onClick={closeMobileSidebar}
            aria-label="Close menu"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Mobile User Profile Section */}
        {isLoggedIn && (
          <div className="mobile-user-profile-section">
            <div className="mobile-user-avatar-large">{getAvatarLetter()}</div>
            <div className="mobile-user-info">
              <strong>{userName}</strong>
              <span>Welcome back!</span>
            </div>
          </div>
        )}

        {/* Mobile Navigation */}
        <nav className="mobile-nav">
          {isLoggedIn && (
            <>
              <Link
                to="/profile"
                className="mobile-nav-link"
                onClick={closeMobileSidebar}
              >
                <span className="mobile-nav-icon">👤</span>
                My Profile
              </Link>
              <Link
                to="/order-history"
                className="mobile-nav-link"
                onClick={closeMobileSidebar}
              >
                <span className="mobile-nav-icon">📦</span>
                Order History
              </Link>
              {/* ✅ ADD WISHLIST HERE - Same as desktop dropdown */}
              <Link
                to="/wishlist"
                className="mobile-nav-link"
                onClick={closeMobileSidebar}
              >
                <span className="mobile-nav-icon">❤️</span>
                Wishlist
              </Link>

              {/* ✅ ADD DIVIDER for visual separation */}
              <div className="mobile-nav-divider"></div>
            </>
          )}

          {getNavigationLinks().map((link, index) => (
            <Link
              key={index}
              to={link.path}
              className="mobile-nav-link"
              onClick={closeMobileSidebar}
            >
              {link.label}
              {link.badge && <span className="mobile-badge">{link.badge}</span>}
            </Link>
          ))}
        </nav>

        {/* Mobile Auth Section */}
        <div className="mobile-auth-section">
          {isLoggedIn ? (
            <button className="mobile-logout-btn" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          ) : (
            <div className="mobile-auth-buttons">
              <Link
                to="/sign-in"
                className="mobile-auth-btn sign-in-btn"
                onClick={closeMobileSidebar}
              >
                <i className="fas fa-sign-in-alt"></i>
                Login
              </Link>
              <Link
                to="/sign-up"
                className="mobile-auth-btn sign-up-btn"
                onClick={closeMobileSidebar}
              >
                <i className="fas fa-user-plus"></i>
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {showMobileSidebar && (
        <div className="mobile-overlay" onClick={closeMobileSidebar}></div>
      )}
    </>
  );
};

export default Header;
